import * as turf from "@turf/turf"
import type { Point, Polygon } from "geojson"
import { memo, useCallback, useMemo } from "react"
import { v4 as uuid } from "uuid"

import { useList } from "../../hooks/utils"
import {
	DRAW_CIRCLE_LAYER_PAINT,
	DRAW_FILL_LAYER_PAINT,
	DRAW_LINE_LAYER_PAINT,
	LINE_STRING_GEOMETRY_TYPE_FILTER,
	POINT_GEOMETRY_TYPE_FILTER,
	POLYGON_GEOMETRY_TYPE_FILTER,
} from "../constants"
import { MapSlotLayerId } from "../enums"
import { useCurrentMapEvent } from "../hooks"
import type { MapEventHandler, UUIDFeature, UUIDFeatureCollection } from "../typings"
import { wrapGeometryInFeature } from "../utils"
import { Layer, Source } from "./memo"

const POLYGON_DRAWER_SOURCE_ID = "polygon-drawer-source"

enum PolygonDrawerLayedId {
	CIRCLE = "polygon-drawer-circle-layer",
	LINE = "polygon-drawer-line-layer",
	FILL = "polygon-drawer-fill-layer",
}

interface PolygonDrawerProps {
	onComplete?: (polygon: Polygon) => void
	onCancel?: () => void
}

export const PolygonDrawer = memo<PolygonDrawerProps>((props) => {
	const { onComplete } = props

	const { items: points, add: addPoint, removeBy: removePointBy } = useList<UUIDFeature<Point>>([])

	const handlePolygonDrawLayerClick = useCallback<MapEventHandler<"click">>(
		(e) => {
			const { point, target, lngLat } = e

			const features = target.queryRenderedFeatures(point, {
				layers: [PolygonDrawerLayedId.CIRCLE],
			})

			const feature = features.length > 0 ? features[0] : undefined

			if (points.length > 0 && feature && feature.id === points[0].properties.uuid) {
				const polygon = turf.lineToPolygon(turf.lineString(points.map((point) => point.geometry.coordinates)), {
					autoComplete: true,
				})

				// @ts-expect-error REASON: polygon is a singular Polygon feature
				onComplete?.(polygon.geometry)
			} else if (feature) {
				removePointBy(({ properties }) => properties?.uuid !== feature.id)
			} else {
				addPoint(
					wrapGeometryInFeature(turf.point(lngLat.toArray()).geometry, {
						uuid: uuid(),
					}),
				)
			}
		},
		[addPoint, onComplete, points, removePointBy],
	)

	useCurrentMapEvent("click", handlePolygonDrawLayerClick)

	const geojson = useMemo<UUIDFeatureCollection>(() => {
		const features: UUIDFeature[] = []

		if (points.length > 0) {
			features.push(...points)
		}

		if (points.length >= 2) {
			features.push(turf.lineString(points.map((point) => point.geometry.coordinates)))
		}

		if (points.length > 2) {
			features.push(
				// @ts-expect-error REASON: polygon is a singular Polygon feature
				turf.lineToPolygon(turf.lineString(points.map((point) => point.geometry.coordinates)), {
					autoComplete: true,
				}),
			)
		}

		return turf.featureCollection(features)
	}, [points])

	return (
		<Source id={POLYGON_DRAWER_SOURCE_ID} type="geojson" data={geojson} promoteId="uuid">
			<Layer
				id={PolygonDrawerLayedId.CIRCLE}
				type="circle"
				paint={DRAW_CIRCLE_LAYER_PAINT}
				filter={POINT_GEOMETRY_TYPE_FILTER}
				beforeId={MapSlotLayerId.DRAW}
			/>
			<Layer
				id={PolygonDrawerLayedId.LINE}
				type="line"
				paint={DRAW_LINE_LAYER_PAINT}
				filter={LINE_STRING_GEOMETRY_TYPE_FILTER}
				beforeId={PolygonDrawerLayedId.CIRCLE}
			/>
			<Layer
				id={PolygonDrawerLayedId.FILL}
				type="fill"
				paint={DRAW_FILL_LAYER_PAINT}
				filter={POLYGON_GEOMETRY_TYPE_FILTER}
				beforeId={PolygonDrawerLayedId.LINE}
			/>
		</Source>
	)
})
PolygonDrawer.displayName = "RectangleDrawer"
